import i18n from "i18next";
import { initReactI18next } from "react-i18next";

/*
 * Translations live one file per LANGUAGE under ./i18n/locales, and are fetched
 * on demand. They used to live one file per FEATURE with all 11 languages inside
 * each — which is why none of it could ever be split: a Portuguese user
 * downloaded Estonian, Finnish and Danish, ~1.3 MB they would never read. The
 * bundler cannot slice a module; only a module boundary can.
 *
 * The per-section `?? X.en` fallbacks the old assembly applied are baked into
 * each locale file at generation time (scripts/split-i18n.ts), so every file is
 * complete on its own. fallbackLng stays "en" anyway, as insurance against a key
 * added to one language and forgotten in another — it costs one extra chunk for
 * non-English users, and silently rendering a raw key would be worse.
 */
const supportedLanguages = ["pt", "en", "es", "et", "de", "fr", "it", "nl", "sv", "da", "fi"];

/*
 * A backend, rather than awaiting the import at each call site. i18next resolves
 * a language's resources BEFORE it emits languageChanged, so changeLanguage()
 * keeps rendering the old language until the new one has landed — no flash of
 * raw keys, and the five callers of changeLanguage() need no changes.
 *
 * The import is a template literal on purpose: Vite turns it into a glob over
 * ./i18n/locales/*.ts and emits one chunk per language.
 */
const lazyLocaleBackend = {
  type: "backend" as const,
  init: () => {},
  read: (
    language: string,
    _namespace: string,
    callback: (error: unknown, resources: unknown) => void,
  ) => {
    import(`./i18n/locales/${language}.ts`)
      .then((module) => callback(null, module.default))
      .catch((error) => callback(error, null));
  },
};

const getInitialLanguage = () => {
  try {
    const stored = localStorage.getItem("moneylights-language");
    if (stored && supportedLanguages.includes(stored)) {
      return stored;
    }
  } catch {
    // ignore storage errors
  }

  try {
    const browserLang = navigator.language.split("-")[0];
    if (supportedLanguages.includes(browserLang)) {
      return browserLang;
    }
  } catch {
    // ignore navigator errors
  }

  return "en";
};

/*
 * The locale whose plural rules a language uses. i18next asks Intl.PluralRules for
 * the bare code, and Intl's "pt" is Brazilian: 0 is singular there ("0 ligação
 * bancária"). Our Portuguese is pt-PT, where 0 is plural. Only the RULES move —
 * the language stays "pt", so resources, detection and the stored choice are
 * untouched.
 */
export const pluralLocale = (language: string) => (language.split("-")[0] === "pt" ? "pt-PT" : language);

// i18next has no option for this; its resolver is created by init(), synchronously.
function applyPluralLocales(): void {
  const resolver = i18n.services.pluralResolver;
  const getRule = resolver.getRule.bind(resolver);
  resolver.getRule = (code: string, options?: object) => getRule(pluralLocale(code), options);
}

/*
 * Resolves once the initial language is in memory. index.jsx awaits it before the
 * first render — with resources now arriving over the network, rendering first
 * would paint raw keys for a frame.
 */
export const initI18n = () => {
  const initialized = i18n
    .use(lazyLocaleBackend)
    .use(initReactI18next)
    .init({
      lng: getInitialLanguage(),
      fallbackLng: "en",
      supportedLngs: supportedLanguages,
      ns: ["translation"],
      defaultNS: "translation",
      // {{agentName}} and {{agent}} are global interpolation defaults so every string
      // renders the agent's name. That name is the product's own role name
      // ({{agent_generic}} — "Financial AI Agent"), translated per language; an org
      // cannot rename its agent, so these only ever change with the language.
      interpolation: {
        escapeValue: false,
        defaultVariables: {
          agentName: "Financial AI Agent",
          agent: "Financial AI Agent",
          agentThe: "Financial AI Agent",
          agentTheCap: "Financial AI Agent",
          agentOf: "Financial AI Agent",
          agentTo: "Financial AI Agent",
        },
      },
      react: { useSuspense: false },
    });
  // Before anything can translate: the first t() runs once the language has landed.
  applyPluralLocales();
  return initialized.then(() => {
    // Both of these used to run at module scope, when init() was synchronous
    // because every language rode in the bundle. It no longer is — at module
    // scope i18n.language is undefined and t() has nothing to read — so they
    // belong here, after the initial language has actually landed.

    // Resolve the generic agent term for the initial language.
    applyAgentVars();

    // `languageChanged` only fires on an actual CHANGE, never on init, so the
    // first load has to sync <html lang> itself — screen readers and SEO read it.
    try {
      document.documentElement.lang = i18n.language;
    } catch {
      // ignore DOM access errors
    }
  });
};

// Gendered "article + name" phrases, so gendered languages (mainly PT) read
// correctly. The agent's name is the role name, which is grammatically masculine
// in Portuguese ("o Agente IA Financeiro"), so the articles are fixed.
function buildComposites(name: string, lang: string) {
  const isPt = lang.split("-")[0] === "pt";
  if (isPt) {
    return { the: `o ${name}`, theCap: `O ${name}`, of: `do ${name}`, to: `ao ${name}` };
  }
  // Other languages don't use gendered articles before the name in our copy.
  return { the: name, theCap: name, of: name, to: name };
}

// Push the agent's name + gendered composites into the interpolation defaults.
// The name is always the translated role term, so this is recomputed on every
// language change. Never emits (callers decide when to notify consumers).
function applyAgentVars(): void {
  const resolved = i18n.t("assistant.agent_generic");
  const comp = buildComposites(resolved, i18n.language);
  const options = i18n.options.interpolation ?? (i18n.options.interpolation = {});
  options.defaultVariables = {
    ...(options.defaultVariables ?? {}),
    agentName: resolved,
    agent: resolved,
    agentThe: comp.the,
    agentTheCap: comp.theCap,
    agentOf: comp.of,
    agentTo: comp.to,
  };
}


i18n.on("languageChanged", (lng) => {
  // Refresh the gender-safe {{agent}} term for the (possibly new) language.
  applyAgentVars();
  try {
    localStorage.setItem("moneylights-language", lng);
    document.documentElement.lang = lng;
  } catch {
    // ignore storage errors
  }
});

export default i18n;
